// src/components/Layout/CanvasArea/PresentationMode.tsx
import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { X, Trash2, Pencil } from 'lucide-react';

interface PresentationModeProps {
  children: React.ReactNode;
  onExit: () => void;
  requestFullscreen?: boolean;
}

export const PresentationMode: React.FC<PresentationModeProps> = ({
  children,
  onExit,
  requestFullscreen = true
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const laserPointerRef = useRef<HTMLDivElement>(null);
  const [drawMode, setDrawMode] = useState(false);
  const isDrawingRef = useRef(false);

  // Request fullscreen on the container element (useLayoutEffect runs synchronously)
  useLayoutEffect(() => {
    if (requestFullscreen && containerRef.current && document.fullscreenEnabled) {
      // Call immediately - useLayoutEffect keeps us in the event chain
      containerRef.current.requestFullscreen().catch(() => {
        // Silently handle - presentation works without fullscreen
      });
    }
  }, [requestFullscreen]);

  // Handle keyboard exit and cleanup
  useEffect(() => {
    // Handle ESC key to exit presentation mode
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onExit();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Exit fullscreen on component unmount if active
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {
          // Silently handle exit errors
        });
      }
    };
  }, [onExit]);

  // Setup canvas size
  useEffect(() => {
    const updateCanvasSize = () => {
      if (canvasRef.current) {
        // Use window dimensions for both fullscreen and window mode
        const width = window.innerWidth;
        const height = window.innerHeight;
        canvasRef.current.width = width;
        canvasRef.current.height = height;
      }
    };

    // Initial size
    updateCanvasSize();

    // Update on resize and fullscreen change
    window.addEventListener('resize', updateCanvasSize);
    document.addEventListener('fullscreenchange', updateCanvasSize);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      document.removeEventListener('fullscreenchange', updateCanvasSize);
    };
  }, []);

  // Track mouse position for laser pointer
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Updating the element directly avoids a React render for every mouse
      // event while presenting. Pointer movement can otherwise cause dozens of
      // renders per second even though the diagram itself has not changed.
      if (laserPointerRef.current) {
        laserPointerRef.current.style.transform =
          `translate3d(${e.clientX - 16}px, ${e.clientY - 16}px, 0)`;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Drawing functions
  const startDrawing = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawMode || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    isDrawingRef.current = true;
  }, [drawMode]);

  const draw = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawMode || !isDrawingRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.strokeStyle = '#ff4444';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [drawMode]);

  const stopDrawing = useCallback(() => {
    isDrawingRef.current = false;
  }, []);

  const clearDrawing = useCallback(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, []);

  const handleExit = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(console.error);
    }
    onExit();
  };

  const toggleDrawMode = () => {
    setDrawMode(!drawMode);
    if (drawMode) {
      // Exiting draw mode, clear any drawings
      clearDrawing();
    }
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9999] bg-white dark:bg-slate-950"
      style={{ cursor: drawMode ? 'crosshair' : 'none' }}
    >
      {/* Main content */}
      <div className="w-full h-full flex">
        {children}
      </div>

      {/* Drawing overlay canvas */}
      <canvas
        ref={canvasRef}
        aria-label="Presentation drawing surface"
        className="absolute inset-0 z-50"
        style={{
          // This surface must always intercept canvas input. When drawing is
          // disabled it acts as a transparent read-only shield; when enabled
          // the same input is used for annotations.
          pointerEvents: 'auto',
          cursor: drawMode ? 'crosshair' : 'none',
          touchAction: 'none'
        }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
      />

      {/* Laser pointer effect - only show when not in draw mode */}
      {!drawMode && (
        <div
          ref={laserPointerRef}
          className="fixed pointer-events-none z-[55] w-8 h-8"
          style={{
            left: 0,
            top: 0,
            transform: 'translate3d(-32px, -32px, 0)',
            willChange: 'transform'
          }}
        >
          {/* Outer glow */}
          <div className="absolute inset-0 w-8 h-8">
            <div className="w-full h-full rounded-full bg-red-500 opacity-30 blur-md animate-pulse" />
          </div>
          {/* Inner dot */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-red-500 shadow-lg shadow-red-500/50" />
        </div>
      )}

      {/* Control panel */}
      <div className="fixed top-4 right-4 flex gap-2 z-[60]">
        {/* Draw mode toggle */}
        <button
          onClick={toggleDrawMode}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg transition-all ${
            drawMode
              ? 'bg-red-500 hover:bg-red-600 text-white'
              : 'bg-white/90 hover:bg-white text-gray-700 dark:bg-slate-800/90 dark:hover:bg-slate-800 dark:text-gray-200'
          }`}
          title={drawMode ? 'Exit Draw Mode' : 'Enable Draw Mode'}
        >
          <Pencil className="w-4 h-4" />
          <span className="text-sm font-medium">
            {drawMode ? 'Drawing' : 'Draw'}
          </span>
        </button>

        {/* Clear drawing - only show in draw mode */}
        {drawMode && (
          <button
            onClick={clearDrawing}
            className="p-2 rounded-lg bg-white/90 hover:bg-white shadow-lg text-gray-700 dark:bg-slate-800/90 dark:hover:bg-slate-800 dark:text-gray-200 transition-all"
            title="Clear Drawing"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}

        {/* Exit button */}
        <button
          onClick={handleExit}
          className="p-2 rounded-lg bg-white/90 hover:bg-white shadow-lg text-gray-700 dark:bg-slate-800/90 dark:hover:bg-slate-800 dark:text-gray-200 transition-all"
          title="Exit Presentation Mode (Esc)"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Help text */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60]">
        <div className="px-4 py-2 rounded-lg bg-black/70 text-white text-sm backdrop-blur-sm">
          {drawMode ? (
            <span>Click and drag to draw • Press ESC or click Exit to leave presentation mode</span>
          ) : (
            <span>Press ESC or click Exit to leave presentation mode • Enable Draw to annotate</span>
          )}
        </div>
      </div>
    </div>
  );
};
